import { CSSProperties, forwardRef, ReactNode } from "react";

import { cn } from "@/ui";

export type SetupListCardProps = {
  /** Optional leading slot rendered before the content (e.g. a drag handle). */
  leading?: ReactNode;
  /** Main content of the row — typically a `SetupCardFields`. */
  children: ReactNode;
  /**
   * Optional trailing slot rendered after a vertical divider (kebab menu, edit
   * button). Omitted entirely when not provided.
   */
  actions?: ReactNode;
  /** ARIA role for the root (e.g. `"listitem"` inside a `role="list"` container). */
  role?: string;
  className?: string;
  style?: CSSProperties;
};

/**
 * Presentational grey rounded row used by the setup-style lists (League setup
 * Seasons / Regions / Levels / Custom tags, ranking category Additional rules).
 * A caller-provided leading slot (drag handle), a flexible content slot, and a
 * trailing actions slot behind a `border-l` divider. Forwards its ref/style so
 * dnd-kit can drive it as a sortable node.
 */
export const SetupListCard = forwardRef<HTMLDivElement, SetupListCardProps>(
  ({ leading, children, actions, role, className, style }, ref) => (
    <div
      ref={ref}
      style={style}
      role={role}
      className={cn(
        "flex items-center gap-4 rounded-lg border border-solid border-surface-2 bg-surface-2 py-4 pl-4 pr-0 sm:py-6 sm:pl-5",
        className
      )}
    >
      {leading}

      <div className="flex-1 min-w-0">{children}</div>

      {actions && (
        <div className="flex shrink-0 items-center self-stretch border-l border-solid border-surface-3 px-2.5">
          {actions}
        </div>
      )}
    </div>
  )
);

SetupListCard.displayName = "SetupListCard";
